import { NumberConstants } from '@/src/constants/general/numbers';
import { StringConstants } from '@/src/constants/general/string';
import { symbolUIStrings } from '@/src/content/symbolContent';

export function truncateNumber(number: number | string | null, digits: number = NumberConstants.SIX) {
  if (number === null) return 'NA';
  let numStr = numberToString(number).replace(/^0+(?=\d)/, '');
  if (numStr.startsWith('.')) {
    numStr = '0' + numStr;
  }
  const [whole, decimal] = numStr.split('.');
  if (!decimal || +numStr === NumberConstants.ZERO) return whole;

  const firstNonZeroIndex = decimal.search(/[1-9]/);

  if (
    firstNonZeroIndex === NumberConstants.NEGATIVE_ONE ||
    (Number(whole) > NumberConstants.ZERO && firstNonZeroIndex > digits)
  ) {
    return whole;
  }

  const sliceTo =
    firstNonZeroIndex < digits
      ? digits
      : firstNonZeroIndex >= digits
        ? firstNonZeroIndex + NumberConstants.ONE
        : digits + NumberConstants.ONE;

  const truncatedNumber = `${whole}.${decimal.slice(NumberConstants.ZERO, sliceTo)}`;

  return Number(truncatedNumber) === NumberConstants.ZERO ? StringConstants.ZERO : truncatedNumber;
}
export const numberToString = (x: number | string): string => {
  const number = x.toString();
  if (number.includes('e-')) {
    const mantissaExponentSplit = number.split('e-');
    const exponent = parseInt(mantissaExponentSplit[NumberConstants.ONE], NumberConstants.TEN);
    const [beforeDecimal, afterDecimal] = mantissaExponentSplit[NumberConstants.ZERO].split('.');
    const beforeDecimalLength = beforeDecimal.length;
    const zeroes = StringConstants.ZERO.repeat(exponent - beforeDecimalLength);
    return `${StringConstants.ZERO}.${zeroes}${beforeDecimal}${afterDecimal ? afterDecimal : StringConstants.EMPTY}`;
  }
  if (number.includes('e+')) {
    const valueExponentSplit = number.split('e+');
    const exponent = parseInt(valueExponentSplit[NumberConstants.ONE], NumberConstants.TEN);
    const [beforeDecimal, afterDecimal] = valueExponentSplit[NumberConstants.ZERO].split('.');
    const afterDecimalLength = afterDecimal ? afterDecimal.length : NumberConstants.ZERO;
    if (afterDecimalLength > exponent) {
      return `${beforeDecimal}${afterDecimal}`;
    }
    const zeroes = StringConstants.ZERO.repeat(exponent - afterDecimalLength);
    return `${beforeDecimal}${afterDecimal ? afterDecimal : StringConstants.EMPTY}${zeroes}`;
  }
  return number;
};

export const countLeadingZeros = (inputString: string) => {
  const firstNonZeroIndex = inputString.search(/[^0]/);
  return firstNonZeroIndex === NumberConstants.NEGATIVE_ONE ? inputString.length : firstNonZeroIndex;
};

export const getSignedValue = (value: number): string => {
  if (value > NumberConstants.ZERO) return `${symbolUIStrings.plus}${Math.abs(value)}`;
  if (value < NumberConstants.ZERO) return `${symbolUIStrings.minus}${Math.abs(value)}`;
  return `${value}`;
};

export const formatPriceImpactForDisplay = (priceImpactPercent: string | null | undefined): string => {
  const num = parseFloat(priceImpactPercent ?? StringConstants.EMPTY);
  const rounded = Math.round(num * NumberConstants.HUNDRED) / NumberConstants.HUNDRED;
  return isNaN(num) || rounded === NumberConstants.ZERO
    ? NumberConstants.ZERO_WITH_TWO_DECIMALS
    : num.toFixed(NumberConstants.TWO);
};

export function toNumber(value?: string): number {
  const num = Number(value ?? '0');
  return Number.isFinite(num) ? num : 0;
}

export function toBigIntSafe(value?: string): bigint | null {
  if (!value) return 0n;
  try {
    return BigInt(value);
  } catch {
    return null;
  }
}

export const formatNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '0';

  const absNum = Math.abs(num);

  if (absNum >= 1e9) {
    return `${(num / 1e9).toFixed(NumberConstants.FOUR)}B`;
  }
  if (absNum >= 1e6) {
    return `${(num / 1e6).toFixed(NumberConstants.FOUR)}M`;
  }
  if (absNum >= 1e4) {
    return `${(num / 1e3).toFixed(NumberConstants.FOUR)}K`;
  }

  return num.toString();
};
